// =============================================================================
// Pattern content, narrative copy per pattern.
//
// Source of truth: iron-lab-explainer/content/pattern-narratives.md. This file
// mirrors that editorial content into a typed TypeScript constant so the
// results-page renderer + PDF generator can consume it at runtime.
//
// Editorial workflow: changes land in pattern-narratives.md first (Isabelle's
// canonical copy), then are mirrored here. A future build script may auto-
// generate this file from the markdown; for now the mirror is maintained
// by hand.
//
// Template variables: string fields may contain `{{variable_name}}`
// placeholders that the renderer interpolates from the Classification +
// LabInputs at render time. The set of supported variables is documented
// in pattern-narratives.md § Template variables reference.
// =============================================================================

import type { WinnerPatternId } from './arbitrate.js';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/** One question in the per-pattern Question Prompt List for the clinician. */
export interface QplEntry {
  /** The short question ("Ask about..."). Rendered bold. */
  question: string;
  /** The one-sentence explainer that follows. */
  explainer: string;
  /**
   * Optional: skip this question when the user already provided all the
   * listed input markers. Used to avoid recommending tests the user already
   * has (e.g., "Can we add TSAT?" when TSAT is already entered).
   */
  skipIf?: {
    hasAllOf?: Array<keyof Pick<LabInputsFields, 'ferritin' | 'tsat' | 'serumIron' | 'tibc' | 'stfr' | 'retHe' | 'crp' | 'rdw' | 'mcv' | 'mch' | 'hemoglobin'>>;
  };
}

/** Phantom type — only used to key skipIf against valid LabInputs fields. */
interface LabInputsFields {
  ferritin: number;
  tsat: number;
  serumIron: number;
  tibc: number;
  stfr: number;
  retHe: number;
  crp: number;
  rdw: number;
  mcv: number;
  mch: number;
  hemoglobin: number;
}

/** Short headline + body shown as the top-of-page banner. */
export interface PatternBanner {
  /** Bold headline, usually mirrors the layperson name. */
  headline: string;
  /** Supporting sentence underneath. */
  body: string;
}

/**
 * All per-pattern narrative content for the results page + PDF.
 * Every field is required, keeps the content contract tight and
 * prevents accidental blanks in production.
 */
export interface PatternContent {
  /** Page-top banner (shown above the abstract / disclaimer). */
  banner: PatternBanner;

  /**
   * Overview paragraphs, the "What your labs are telling us" section.
   * Each array entry is one paragraph. Supports `{{template_vars}}`.
   */
  overview: string[];

  /**
   * Symptom integration narrative, rendered only when the user reported
   * symptoms in step 2. Each array entry is one paragraph — break dense
   * copy into short paragraphs for scannability. If the pattern has nothing
   * pattern-specific to say about symptoms, set to `undefined`.
   */
  symptomIntegration?: string[];

  /**
   * One- or two-sentence summary rendered in the abstract's "Next steps"
   * cell at the top of the results page. Must stand alone (no trailing
   * colon or list-intro fragment). Pattern-specific; do not reuse
   * `whatThisMeansIntro` here — that string ends with a colon because it
   * introduces the QPL below, which looks broken in the abstract.
   */
  abstractNextSteps: string;

  /**
   * Short intro paragraph that precedes the Question Prompt List in the
   * "What comes next" section. Supports template vars. Ends with a colon
   * because the QPL renders immediately below.
   */
  whatThisMeansIntro: string;

  /**
   * Per-pattern Question Prompt List, the questions the user should bring
   * to a clinician. Rendered as bulleted list; also emitted into the PDF.
   */
  questionPromptList: QplEntry[];

  /**
   * Confidence tier paragraph, describes how strong the engine's read is.
   * Supports template vars (e.g., `{{confidence_description}}`). Renderer
   * may conditionalize on `Classification.confidenceTier`.
   */
  confidenceParagraph: string;

  /** Short copy introducing the PDF download CTA. */
  pdfTransition: string;
}

// -----------------------------------------------------------------------------
// Part 1. Iron staged model
// -----------------------------------------------------------------------------

const stage_0_iron_replete: PatternContent = {
  banner: {
    headline: 'Your iron levels look healthy.',
    body: 'Based on the values you entered, your iron stores are in the range the research calls replete, well-stocked and stable.',
  },
  overview: [
    'Your ferritin came back at **{{ferritin_value}}**. For {{life_stage_friendly}}, the research-based threshold for replete iron stores is **at or above {{stage_0_threshold}} {{units}}**. Your value sits in that range.',
    'The evidence for this threshold is `HIGH QUALITY`. It comes from studies that measured iron absorption directly, and identified the ferritin level at which the body stops working harder to absorb iron, meaning stores are sufficient and the absorption machinery has returned to baseline.',
    '**Your body is not signaling iron hunger.** The protein that stores iron is at a level the research associates with enough raw material for red blood cell production, with some reserve on top. This is the pattern we expect to see in someone whose intake, losses, and absorption are roughly in balance.',
    '**A note on what this does and does not mean.** "Replete" is a research term for iron stores specifically, it does not speak to every nutrient, every marker, or every symptom.',
    'If you came to this tool because you\'re experiencing fatigue, hair changes, or other concerns, a normal iron read does not mean those concerns aren\'t real. It means iron is unlikely to be the driver, and the conversation with your clinician shifts to what else could be.',
  ],
  symptomIntegration: [
    'You told us you\'re experiencing **{{listed_symptoms_plain_language}}**. Your iron labs look healthy, so iron is unlikely to be the main driver of those symptoms.',
    'That doesn\'t mean nothing is going on. It means the research-based iron markers are not where we\'d look next. Thyroid function, B12, folate, vitamin D, sleep quality, and mental health all sit in the differential when iron is clear.',
    'Your clinician is the right person to map this. We are not saying you are fine. We are saying iron is probably not the reason.',
  ],
  abstractNextSteps:
    'No iron-specific follow-up needed.',
  whatThisMeansIntro:
    'Nothing in this pattern requires iron-specific follow-up. If you want to frame the result with a clinician anyway, these questions may be useful:',
  questionPromptList: [
    {
      question: 'How often should I recheck my iron?',
      explainer:
        'For most people with healthy iron stores, routine rechecks aren\'t needed unless something changes, heavy periods, pregnancy, new symptoms, or a new health condition.',
    },
    {
      question: 'If I still have symptoms, what else should we look at?',
      explainer:
        'If you came in with fatigue or another concern, a normal iron read narrows the differential to things like thyroid, B12, folate, vitamin D, sleep, or mental health.',
    },
    {
      question: 'When does life-stage context change things?',
      explainer:
        'Iron needs shift across the reproductive years, pregnancy, postpartum, and menopause. What looks healthy now may need rechecking at a transition point.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in this read. The replete threshold is `HIGH QUALITY` evidence anchored in iron-absorption physiology and multinational cohorts, and your other values track with this pattern rather than pointing elsewhere.',
  pdfTransition:
    'Your PDF includes the full version of this read, a few questions worth bringing to your clinician if you still have symptoms, and the research citations behind the thresholds we applied.',
};

const stage_1_incipient_id: PatternContent = {
  banner: {
    headline: 'Early signs of your iron running low.',
    body: 'Your ferritin sits below the level where the research sees iron stores as stable, but above the level that flags active shortage. This is the earliest stage the research describes, and it\'s the stage a standard lab range will almost always miss.',
  },
  overview: [
    'Your ferritin came back at **{{ferritin_value}}**. For {{life_stage_friendly}}, the research-based threshold for replete iron stores is **at or above {{stage_0_threshold}} {{units}}**. Below that, the body begins a physiological response: iron absorption starts turning up. Your value is in that early-response zone, above the threshold for outright shortage but below the stable range.',
    'The evidence for this inflection is `HIGH QUALITY`. Studies that measured iron absorption directly identified this exact ferritin level as the point at which the hormone that controls iron absorption (hepcidin) begins to drop and absorption climbs.',
    '**Your body is starting to notice that iron is tight.** Red blood cell production still has enough raw material, so hemoglobin is normal and a standard CBC looks fine. What\'s shifted is your body\'s effort to keep up. It\'s working a little harder to hold the line.',
    'The most common reason this stage gets missed is that laboratory reference ranges were built decades ago from populations that included many women with low iron stores. That made the "normal" lower bound artificially low. The research over the past five years has made a strong case that the real physiological threshold is higher, and your value falls in the zone the research now flags and the old ranges don\'t.',
  ],
  symptomIntegration: [
    'You told us you\'re experiencing **{{listed_symptoms_plain_language}}**. The research on symptoms at this stage is genuinely mixed. Some studies find associations between early ferritin decline and fatigue or exercise capacity; others don\'t.',
    'The clearest signals come in studies of women who were specifically recruited for a fatigue complaint. When those women had ferritin under 50, iron interventions sometimes produced measurable improvements. That\'s worth your clinician\'s time to evaluate.',
  ],
  abstractNextSteps:
    'Worth raising with a clinician at your next appointment.',
  whatThisMeansIntro:
    'This pattern is a good reason for a clinical conversation, not an urgent one. Things worth bringing to your clinician:',
  questionPromptList: [
    {
      question: 'Can you pull any past iron labs from my chart so we can see the trajectory?',
      explainer:
        'Trajectory tells the story. A ferritin of 40 that used to be 90 reads differently from a ferritin of 40 that has been stable for years. Your clinician may have prior results on file that you don\'t have copies of.',
    },
    {
      question: 'What could be driving this?',
      explainer:
        'In menstruating women, menstrual blood loss is the most common driver. Diet, absorption conditions (celiac, H. pylori, acid-blocking medications), and other factors also matter.',
    },
    {
      question: 'When should we retest?',
      explainer:
        'Most clinicians will recommend a follow-up measurement in a few months to see which direction this is moving.',
    },
    {
      question: 'Should we add a fuller iron panel?',
      explainer:
        'Adding TSAT would help confirm the picture beyond ferritin alone.',
      skipIf: { hasAllOf: ['tsat'] },
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in this read. The threshold is `HIGH QUALITY` evidence, validated across absorption, hepcidin, and clinical-cohort studies. This stage is frequently missed by standard reference ranges, which is why we surface it.',
  pdfTransition:
    'Your PDF includes the full version of this read, the specific questions to bring to your clinician, and the research citations behind every threshold we applied.',
};

const stage_2_iron_depletion_no_anemia: PatternContent = {
  banner: {
    headline: 'Your iron reserves are running low.',
    body: 'Your ferritin is below the threshold the research uses for iron depletion, but above the threshold where red blood cell production starts getting constrained. Hemoglobin is still in the normal range, this is not anemia.',
  },
  overview: [
    'Your ferritin came back at **{{ferritin_value}}**. For {{life_stage_friendly}}, the research-based depletion threshold is **below {{stage_2_threshold}} {{units}}**. Your value is in the depletion range, iron reserves are genuinely low, but not yet in the range where red blood cell production starts to feel the shortage. The evidence for this threshold is `HIGH QUALITY`.',
    '**Your stored iron is running thin.** The body has been drawing on reserves faster than it has been replacing them. Red blood cell production still has enough iron to work with for now, so hemoglobin reads normal and a standard CBC looks fine.',
    'This is the stage where the research most strongly argues for attention, because the next stage, where red blood cell production starts to feel the shortage, is around the corner.',
    '**This stage is also the most commonly missed by standard lab interpretation.** Reference ranges often don\'t flag values in this zone, because clinical laboratories derive their lower limits from population samples that already include a meaningful share of women in early iron depletion, which pulls the "normal" lower bound below the research-based threshold.',
  ],
  symptomIntegration: [
    'You told us you\'re experiencing **{{listed_symptoms_plain_language}}**. At this stage, symptom associations with low iron are becoming more consistent in the research, particularly fatigue and exercise capacity.',
    'Your clinician can weigh whether iron is the likely driver given your full picture.',
  ],
  abstractNextSteps:
    'Worth a clinical conversation at your next appointment, not urgent.',
  whatThisMeansIntro:
    'This pattern is a clinical conversation, not an urgent one. Things worth bringing to your clinician:',
  questionPromptList: [
    {
      question: 'Can we add TSAT if I don\'t have it?',
      explainer:
        'TSAT tells us about iron delivery in addition to stores and helps confirm the pattern.',
      skipIf: { hasAllOf: ['tsat'] },
    },
    {
      question: 'What could be driving the depletion?',
      explainer:
        'Menstrual blood loss is the most common driver in menstruating women. Diet, absorption conditions, and other factors also matter.',
    },
    {
      question: 'When should we retest?',
      explainer:
        'A follow-up measurement in a few months tells you whether stores are stabilizing, continuing to fall, or rebuilding.',
    },
    {
      question: 'Can you pull any past iron labs from my chart so we can see the trajectory?',
      explainer:
        'Trajectory matters more than any single reading. A ferritin that used to be stable and has just dropped reads differently from one that\'s been low for years. Your clinician may have prior results on file you don\'t have copies of.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in this read. The depletion threshold is `HIGH QUALITY` evidence validated across multinational cohorts and absorption studies, and your other values track with depletion rather than pointing elsewhere.',
  pdfTransition:
    'Your PDF includes the full version of this read, the specific questions to bring to your clinician, and the research citations behind every threshold we applied.',
};

const stage_3_iron_deficient_erythropoiesis: PatternContent = {
  banner: {
    headline: 'Your iron stores are running low.',
    body: 'Hemoglobin is still in the normal range, so this isn\'t anemia yet.',
  },
  overview: [
    'Your ferritin came back at **{{ferritin_value}}**. For {{life_stage_friendly}}, the research-based threshold for this pattern is **below {{stage_3_threshold}} {{units}}**. The evidence for this threshold is `HIGH QUALITY`, a large multinational analysis pooling data from women in twelve countries found this threshold holds up consistently, regardless of population or geography.',
    '**Your body\'s iron storage protein is below the level at which iron supply to the bone marrow starts getting constrained.** Red blood cell production is still happening, but it\'s working with less raw material.',
    'Hemoglobin often stays within the reference range for months or years at this stage before dropping low enough to flag as anemia. Because a standard CBC looks normal, this stage gets missed very often.',
  ],
  symptomIntegration: [
    'You told us you\'re experiencing **{{listed_symptoms_plain_language}}**. The research documents associations between several of these symptoms and ferritin below 30 μg/L.',
    '**Fatigue.** A large review of studies found that iron repletion in women with ferritin below 50 and unexplained fatigue produced measurable improvements in fatigue scores within 6 to 12 weeks.',
    '**Hair shedding.** Telogen effluvium (diffuse hair shedding) has been associated with ferritin below 30 in multiple cohort studies. Restoration of hair density often lags restoration of ferritin by several months.',
  ],
  abstractNextSteps:
    'Clinical conversation warranted to discuss driver and retest plan.',
  whatThisMeansIntro:
    'Your clinician is the right person to evaluate this pattern for you. We\'re not diagnosing or recommending treatment, our job is to translate what the research says about values like yours so you can walk into a clinical conversation prepared. A few things worth bringing to your doctor:',
  questionPromptList: [
    {
      question: 'Can we add the full iron panel if I don\'t have it?',
      explainer:
        'Ferritin is the most informative single marker, but TSAT + serum iron + TIBC together tell a fuller story, especially about whether any low-iron findings are nutritional, from blood loss, from absorption issues, or something else.',
      skipIf: { hasAllOf: ['tsat', 'serumIron', 'tibc'] },
    },
    {
      question: 'What could be driving this?',
      explainer:
        'Low iron stores in menstruating women are most often explained by menstrual blood loss, but dietary intake, absorption conditions (celiac, H. pylori, acid blockers), and other factors also matter.',
    },
    {
      question: 'When should we retest?',
      explainer:
        'Most clinicians will recommend rechecking ferritin in a few months. The timing depends on what (if anything) your doctor recommends you do in the meantime.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in this read. The ferritin threshold is `HIGH QUALITY` evidence, replicated across multiple countries and validated against bone-marrow iron (the gold standard). Your other values are consistent with this pattern rather than pointing elsewhere.',
  pdfTransition:
    'Your PDF includes the full version of this read, the specific questions to bring to your doctor, and the research citations behind every threshold we applied. It\'s the most useful artifact from this tool, save it, print it, bring it.',
};

const stage_4_iron_deficiency_anemia: PatternContent = {
  banner: {
    headline: 'Your results fit the pattern of iron deficiency anemia.',
    body: 'Ferritin is below the threshold for iron shortage, and hemoglobin is below the anemia threshold. Anemia is common, but not something to set aside, finding the cause and starting treatment matters for how you feel day to day and for your longer-term health.',
  },
  overview: [
    'Your ferritin came back at **{{ferritin_value}}** and your hemoglobin at **{{hb_value}}**. For {{life_stage_friendly}}, the research-based ferritin threshold for active iron shortage is **below {{stage_3_threshold}} {{units}}**, and the hemoglobin threshold for anemia is **below {{anemia_threshold}} {{units}}**. Both of your values fall below those thresholds. The evidence for both is `HIGH QUALITY`, with `CONSENSUS` from major health organizations on the hemoglobin cutoff.',
    '**Your iron stores are low enough that red blood cell production has been affected.** Hemoglobin is the oxygen-carrying protein in red blood cells. When iron supply to the bone marrow is constrained long enough, hemoglobin drops, and once it drops below the reference, the research calls the pattern anemia.',
    'Oxygen delivery to tissues is measurably reduced, which is why anemia can produce real effects like fatigue, breathlessness on exertion, and impaired concentration, even at hemoglobin levels that a quick chart-scan might pass over. Iron deficiency is the most common cause of anemia in women worldwide, and it is treatable once the cause is identified.',
    'This is a pattern clinicians see often and have a clear evaluation pathway for. Two things typically drive the conversation: **why** iron stores are low (the cause) and **what** to do about it (the plan). Both are worth getting answers on sooner rather than later.',
  ],
  symptomIntegration: [
    'You told us you\'re experiencing **{{listed_symptoms_plain_language}}**. At this stage, associations between low iron and several symptoms are better established than at earlier stages.',
    'How severely someone feels iron deficiency anemia varies widely. Some people with this pattern feel relatively normal; others feel badly affected. Both experiences are documented in the research.',
    'The severity of symptoms does not map neatly onto the numbers.',
  ],
  abstractNextSteps:
    'Clinical evaluation warranted to confirm the pattern, identify the cause, and start treatment.',
  whatThisMeansIntro:
    'This pattern warrants a clinical conversation and evaluation. The research describes oral iron as the standard first-line approach in most settings, but the right choice depends on cause, severity, absorption, tolerability, and factors a clinician evaluates with you. Things worth bringing to the appointment:',
  questionPromptList: [
    {
      question: 'What is likely driving this?',
      explainer:
        'The most common driver in menstruating women is menstrual blood loss. Gastrointestinal causes, dietary factors, absorption conditions, and pregnancy-related iron demands all sit in the differential.',
    },
    {
      question: 'What does the plan look like?',
      explainer:
        'Options vary, oral iron, intravenous iron, or addressing an underlying cause first. Each has pros, cons, and cost considerations. The right fit benefits from your input on tolerability, preferences, and logistics.',
    },
    {
      question: 'How should we schedule follow-up testing?',
      explainer:
        'After any iron intervention, recheck timing and which labs to recheck are part of the plan.',
    },
    {
      question: 'Can you pull my prior labs from my chart so we can see the trajectory?',
      explainer:
        'Where you were six months or a year ago sharpens the interpretation and the plan. Your clinician may have prior results on file you don\'t have copies of.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in this read. Both thresholds rest on `HIGH QUALITY` or `CONSENSUS` evidence, and your values fit iron deficiency anemia rather than pointing toward alternative causes of low hemoglobin (like anemia of chronic disease or a hemoglobin variant). A clinician integrating your full history and any additional testing confirms the read and leads what happens next.',
  pdfTransition:
    'Your PDF includes the full version of this read, a focused question-prompt list for your clinical visit, and the research citations behind every threshold we applied. The PDF does not contain treatment recommendations. It\'s built to help you walk into the appointment prepared, not to replace it.',
};

// -----------------------------------------------------------------------------
// Part 2. Inflammation patterns
// -----------------------------------------------------------------------------

const inflammation_masks_iron_status: PatternContent = {
  banner: {
    headline: 'Your ferritin can\'t be read at face value right now.',
    body: 'Either your CRP (a marker of inflammation) is elevated, or you told us you\'ve been sick, had surgery, or experienced a chronic-condition flare in the last two weeks. Both of those change how we can read your iron labs.',
  },
  overview: [
    '**Ferritin is the protein that stores iron in your body\'s cells, but it\'s also what the research calls an acute-phase reactant.** When your body is fighting infection, recovering from surgery, or dealing with any kind of inflammation, your ferritin level in the blood rises in response. It rises even if your actual iron stores haven\'t changed.',
    'This isn\'t speculation. The evidence for this mechanism is `HIGH QUALITY`, confirmed both in large observational studies across hundreds of thousands of women and in experimental studies where researchers deliberately induced mild illness in healthy volunteers and watched ferritin climb predictably.',
    'When inflammation is in the picture, your lab results could reflect one of two underlying situations.',
    '- **True iron deficiency masked by inflammation**, iron stores are actually low, but ferritin reads falsely high because of the inflammation.\n- **Functional iron deficiency (anemia of chronic disease pattern)**, iron is actually present in your body, but locked away from the cells that need it.',
    'Distinguishing between these is a clinical conversation, not something we can settle from your entries alone.',
  ],
  abstractNextSteps:
    'Retest ferritin 2 to 4 weeks after acute inflammation resolves.',
  whatThisMeansIntro:
    'If the inflammation you reported was acute and is resolving, a retest in 2–4 weeks is often the simplest path to a clearer read. If you have a chronic inflammatory condition, this pattern may be persistent rather than a snapshot. Questions worth bringing to your clinician:',
  questionPromptList: [
    {
      question: 'Is my inflammation likely masking iron deficiency, or does my iron situation reflect what my labs say?',
      explainer:
        'This is the core question. The answer shapes everything else.',
    },
    {
      question: 'Should we retest in 2–4 weeks after any acute inflammation resolves?',
      explainer:
        'A ferritin drawn when your body isn\'t fighting anything is much more interpretable.',
    },
    {
      question: 'If I have a chronic inflammatory condition, is the pattern here consistent with anemia of chronic disease?',
      explainer:
        'Different condition, different management. The terminology is worth disambiguating explicitly.',
    },
    {
      question: 'Is sTfR or another marker worth adding to my next panel?',
      explainer:
        'sTfR (soluble transferrin receptor) is less affected by inflammation than ferritin and can sharpen the read.',
      skipIf: { hasAllOf: ['stfr'] },
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in the fact that the research on inflammation inflating ferritin is well established. We are deliberately cautious about drawing further conclusions, because interpretation requires clinical judgment.',
  pdfTransition:
    'Your PDF includes everything above plus the specific clinician-conversation prompts for this pattern and a retest scheduling template you can reuse.',
};

const functional_iron_deficiency: PatternContent = {
  banner: {
    headline: 'Your iron appears to be in your body, but locked away from the cells that need it.',
    body: 'This pattern looks different from classical iron deficiency, and the clinical pathway is different too. A clinician conversation is the right next step.',
  },
  overview: [
    'Your ferritin came back at **{{ferritin_value}}** and your TSAT at **{{tsat_value}}**. Either your CRP is elevated, or you told us you live with a chronic inflammatory condition, and that changes how we read your iron labs. The research calls this pattern **functional iron deficiency**. The evidence supporting it is `HIGH QUALITY`, anchored in large studies of dialysis patients and other chronic-disease populations.',
    '**Ferritin measures stored iron, and yours is normal or high. TSAT measures how much iron is actually being delivered to the cells that need it, and yours is low.** In the presence of inflammation, the body produces a hormone called **hepcidin** (the master regulator of iron) at elevated levels. Hepcidin holds iron inside storage cells.',
    'The iron is there, it just can\'t get to where it\'s needed. Red blood cells, muscles, and other tissues see a supply shortage even though the warehouse has stock.',
    '**This pattern is different from iron running low in a classical sense.** The iron is not absent from the body. Taking iron by mouth, the standard first-line approach for classical iron deficiency, often does not work well here, because the same hepcidin that\'s locking iron away also blocks absorption from the gut.',
    'The evaluation focuses on the underlying inflammation or chronic condition, and on whether iron delivered another way (intravenously) is part of the picture.',
  ],
  abstractNextSteps:
    'Different clinical pathway than iron deficiency; evaluation focused on underlying inflammation.',
  whatThisMeansIntro:
    'This pattern routes to a different clinical pathway than classical iron deficiency. Mixing the two up leads to the wrong plan. Questions worth bringing:',
  questionPromptList: [
    {
      question: 'Does my pattern look like functional iron deficiency or anemia of chronic disease?',
      explainer:
        'Your clinician may use these terms interchangeably or distinguish between them; both sit in the same neighborhood.',
    },
    {
      question: 'What\'s the underlying driver?',
      explainer:
        'Chronic inflammatory conditions, chronic kidney disease, heart failure, and other conditions all produce this pattern. Identifying the driver shapes the plan.',
    },
    {
      question: 'Would oral iron likely work, or is intravenous iron a better option?',
      explainer:
        'The research consistently documents that oral iron is often less effective in this pattern.',
    },
    {
      question: 'Are additional markers worth adding?',
      explainer: '{{additional_markers_for_fid}}',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in the flag. The combination of elevated ferritin, low TSAT, and inflammation is a well-characterized signature. We are deliberately cautious about what to do next, because that depends on your underlying condition and clinical context we don\'t have.',
  pdfTransition:
    'Your PDF includes the full version of this read, the clinician-conversation prompts, and the research citations behind every threshold we applied. This is one of the PDFs worth printing and bringing to the appointment.',
};

const anemia_of_chronic_disease_classic: PatternContent = {
  banner: {
    headline: 'Your results fit the pattern of anemia linked to long-term inflammation.',
    body: 'Hemoglobin is below the anemia threshold, but your iron stores are not depleted. Combined with the inflammation signal you have, this points to a different kind of anemia, one the research calls anemia of chronic disease.',
  },
  overview: [
    'Your hemoglobin came back at **{{hb_value}}**, below the reference threshold of **{{anemia_threshold}} {{units}}**. Your ferritin at **{{ferritin_value}}** is not in the depleted range, and your TSAT at **{{tsat_value}}** is below 20 %. Either your CRP is elevated, or you told us you live with a chronic inflammatory condition. The evidence supporting this pattern is `HIGH QUALITY`.',
    '**You have anemia, hemoglobin is low enough to meet the threshold, but the cause does not look like straightforward iron shortage.** Iron is stored. What looks different is iron **delivery**: your TSAT is low, meaning iron is not getting to the cells that need it even though stores are not empty.',
    'In the presence of inflammation, the body produces hepcidin (the iron-regulating hormone) at high levels. Hepcidin holds iron inside storage cells, blocks gut absorption, and shortens red blood cell lifespan. The result is anemia, with a very different origin story than classical iron deficiency.',
    '**This pattern is common in people living with chronic conditions, inflammatory bowel disease, rheumatoid arthritis, chronic kidney disease, heart failure, long COVID, lupus, and others.** It can also appear after prolonged infections.',
    'The two can also **coexist**, someone with a chronic inflammatory condition can also have genuine iron deficiency. Untangling the two is one of the situations where additional markers help.',
  ],
  abstractNextSteps:
    'Primary lever is usually the underlying chronic condition, not iron itself.',
  whatThisMeansIntro:
    'Anemia of chronic disease and iron deficiency anemia can look superficially similar, but they route to different clinical plans. The primary lever for anemia of chronic disease is usually the underlying condition. Questions worth bringing to your clinician:',
  questionPromptList: [
    {
      question: 'Does my pattern look like anemia of chronic disease, iron deficiency, or a combination?',
      explainer:
        'Your clinician may want additional markers to tell.',
    },
    {
      question: 'What\'s the underlying condition driving this?',
      explainer:
        'If you already have a chronic condition diagnosis, ask how this anemia is linked to it. If you don\'t, this pattern is worth exploring.',
    },
    {
      question: 'Would sTfR or another non-ferritin marker help clarify?',
      explainer:
        'sTfR is the single most useful addition for distinguishing this pattern from mixed pictures.',
    },
    {
      question: 'What changes if we address the underlying inflammation?',
      explainer:
        'The primary lever for anemia of chronic disease is usually the underlying condition, not iron itself.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in the flag. The combination of anemia, normal-to-high ferritin, low TSAT, and inflammation is a well-characterized signature. We are deliberately cautious beyond that, because the underlying driver (and whether iron deficiency is also present) requires a fuller clinical picture.',
  pdfTransition:
    'Your PDF includes the full version of this read, the clinician-conversation prompts, and the research citations behind every threshold we applied.',
};

// -----------------------------------------------------------------------------
// Part 3. Overload patterns
// -----------------------------------------------------------------------------

const iron_overload_trigger_premenopausal: PatternContent = {
  banner: {
    headline: 'Your iron markers are above the evaluation threshold.',
    body: 'Your ferritin and your transferrin saturation (TSAT) are both higher than the research-based thresholds used to decide when a woman your age should be evaluated for an iron-overload condition. This is a flag, not a diagnosis.',
  },
  overview: [
    'Your ferritin came back at **{{ferritin_value}}** and your TSAT came back at **{{tsat_value}}**. For premenopausal women, the research-based evaluation thresholds are **ferritin above 200 μg/L** combined with **TSAT above 45%**. Both of yours are above that combined threshold.',
    'The combination of high iron stores (ferritin) and a high share of your iron-carrying protein that\'s loaded with iron (TSAT) is the pattern a clinician uses to decide whether an iron-overload workup is worth doing. The evidence for these thresholds is `HIGH QUALITY` and the guideline position is `CONSENSUS`, the European Association for the Study of the Liver set these cutoffs based on long-running multi-country population data.',
    '**Most elevated ferritin in women is not hemochromatosis.** In the largest screening study of its kind, only about 8 in 100 people with very high ferritin turned out to have the genetic condition.',
    'The rest had other explanations: alcohol intake, liver conditions, metabolic factors, and other causes all produce this same pattern. The point of the workup is to figure out which one applies to you.',
  ],
  abstractNextSteps:
    'Clinician evaluation for overload workup, starting with a fasting morning repeat draw.',
  whatThisMeansIntro:
    'The pattern says: time for a clinician conversation. It does not say: you have a specific condition. A few things worth bringing to your doctor:',
  questionPromptList: [
    {
      question: 'Can we repeat the iron studies on a fasting morning draw before any further workup?',
      explainer:
        'TSAT has real day-to-day variation and is highest in the morning fasting. A single elevated reading is worth confirming under the most interpretable conditions.',
    },
    {
      question: 'What does the differential look like?',
      explainer:
        'Elevated ferritin has several common causes in women. A clinician will weigh the pattern against your history: alcohol intake, liver health, metabolic markers, recent illness, and family history of iron conditions.',
    },
    {
      question: 'Is HFE genotyping indicated, and if so, when?',
      explainer:
        'Genetic testing is not the first step. It usually comes after confirmation and after other causes are weighed. The research does not support universal genetic screening.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in the flag. The combined ferritin + TSAT threshold sits on `HIGH QUALITY` evidence inside a `CONSENSUS` clinical guideline. The pattern triggers evaluation, not a diagnosis. Most women with this pattern do not have hemochromatosis. The workup is what sorts the cause.',
  pdfTransition:
    'Your PDF includes the full version of this read, the question prompt list, and the research citations behind every threshold we applied. Bring it to the appointment.',
};

const iron_overload_trigger_postmenopausal: PatternContent = {
  banner: {
    headline: 'Your iron markers are above the evaluation threshold for your life stage.',
    body: 'Your ferritin and your transferrin saturation (TSAT) are both above the research-based thresholds that a clinician uses to decide when a postmenopausal woman should be evaluated for an iron-overload condition. This is a flag, not a diagnosis.',
  },
  overview: [
    'Your ferritin came back at **{{ferritin_value}}** and your TSAT came back at **{{tsat_value}}**. For postmenopausal women, the research-based evaluation thresholds are **ferritin above 300 μg/L** combined with **TSAT above 50%**. Both of yours are above that combined threshold.',
    '**Why the thresholds are higher postmenopausal: after the final menstrual period, ferritin rises in most women.** The research has tracked this rise in long-running cohorts. A value that would flag in a 30-year-old may sit within the expected range in a 65-year-old.',
    'The `HIGH QUALITY` evidence on postmenopausal iron trajectory is what sets these higher thresholds, and the `CONSENSUS` guideline from the European Association for the Study of the Liver uses them.',
    '**Most elevated ferritin in postmenopausal women is not hemochromatosis.** Alcohol intake, liver conditions, metabolic factors, and other causes all produce this pattern. Women with certain HFE gene variants can also see a faster postmenopausal ferritin rise, which is one of several reasons the workup is worth doing.',
  ],
  abstractNextSteps:
    'Clinician evaluation for overload workup; most elevated ferritin in this life stage is not hemochromatosis.',
  whatThisMeansIntro:
    'A few things worth bringing to your doctor:',
  questionPromptList: [
    {
      question: 'Can we repeat iron studies to confirm?',
      explainer:
        'TSAT varies day to day; a fasting morning draw is the most interpretable.',
    },
    {
      question: 'What does the differential look like for my age group?',
      explainer:
        'Metabolic factors, liver health, and alcohol intake are common contributors to elevated ferritin in older women.',
    },
    {
      question: 'Does my family history change the workup?',
      explainer:
        'A family history of hemochromatosis or unexplained liver disease can shift how a clinician weighs genetic testing.',
    },
    {
      question: 'Can we confirm with a fasting morning repeat draw before going further?',
      explainer:
        'No diagnostic decision should rest on a single draw. A fasting morning repeat is the most interpretable confirmation.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in the flag. The postmenopausal threshold pair sits on `HIGH QUALITY` evidence inside a `CONSENSUS` guideline. The pattern triggers evaluation, not a diagnosis. The cause could be any of several things, and the workup is what sorts them.',
  pdfTransition:
    'Your PDF includes the full narrative, the question prompt list, and citations for every threshold we applied.',
};

const very_high_ferritin_any_stage: PatternContent = {
  banner: {
    headline: 'Your ferritin is very high.',
    body: 'At values above 1000 μg/L, the research-based position is that a clinician should evaluate the cause, regardless of what your other iron markers look like. This is a flag, not a diagnosis. Most causes are not hemochromatosis.',
  },
  overview: [
    'Your ferritin came back at **{{ferritin_value}}**. Above 1000 μg/L is the threshold at which the research recommends evaluation regardless of the rest of the panel. The evidence for this threshold is `HIGH QUALITY`, a large multi-ethnic primary-care screening study is the primary source.',
    '**In that same large study, only about 8 in 100 people with very high ferritin turned out to have the genetic iron-overload condition called hemochromatosis.** The remaining 92 had other explanations.',
    'The most common: alcohol intake, liver conditions (including fatty liver and hepatitis), metabolic factors, ongoing inflammation, and occasionally certain cancers. High ferritin is a signal that something is driving the number up, but the underlying cause varies a lot.',
    '**The pattern across your other iron markers helps narrow the cause.** High ferritin with high TSAT points more toward an iron-overload picture; high ferritin with normal or low TSAT points more toward inflammation, liver disease, or metabolic factors. A clinician sorts out which applies.',
  ],
  abstractNextSteps:
    'See a clinician for evaluation; confirm with a fasting morning repeat draw.',
  whatThisMeansIntro:
    'The pattern says: see a clinician. A few things worth bringing to the appointment:',
  questionPromptList: [
    {
      question: 'Can we repeat ferritin and TSAT on a fasting morning draw?',
      explainer:
        'Draw conditions meaningfully affect interpretation; a confirmatory draw is often part of the first step.',
    },
    {
      question: 'Should liver tests and metabolic markers be part of the workup?',
      explainer:
        'ALT, AST, and related markers help sort out whether the liver is part of the picture; metabolic markers often matter too.',
    },
    {
      question: 'Could any current medications or supplements be contributing?',
      explainer:
        'A medication and supplement review is a simple, high-value first step.',
    },
    {
      question: 'What\'s the order of the workup?',
      explainer:
        'Genetic testing is usually not the first step, confirmation and the differential come first.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in the flag. `HIGH QUALITY` evidence sets 1000 μg/L as the threshold for evaluation regardless of other markers. We are deliberately cautious about the cause, because a full workup with your clinician is the right way to sort through the possibilities.',
  pdfTransition:
    'Your PDF includes the full read, a clinician question list tailored to very high ferritin, and the research citations behind the threshold.',
};

// -----------------------------------------------------------------------------
// Part 4. Morphology patterns
// -----------------------------------------------------------------------------

const microcytic_normal_iron_studies: PatternContent = {
  banner: {
    headline: 'Your red cells are small, but your iron stores look fine.',
    body: 'That points away from iron deficiency. Your clinician can order a test (hemoglobin electrophoresis) to check the next likely cause.',
  },
  overview: [
    'Your MCV came back at **{{mcv_value}}**, below the 80 fL threshold the research uses for microcytic (small) red cells. Your ferritin at **{{ferritin_value}}** and your TSAT at **{{tsat_value}}** are both in the range that does not fit classical iron deficiency. The evidence for this pattern as a distinct flag is `HIGH QUALITY`, anchored in a canonical review of inherited microcytic anemias.',
    '**Small red blood cells in women most often point to iron shortage, when iron is tight, the body produces smaller cells.** But when iron stores are adequate and delivery is not restricted, the explanation for small cells is usually something else.',
    'The most common alternative is a **hemoglobinopathy**, a variation in the structure of hemoglobin itself. Beta-thalassemia trait and alpha-thalassemia trait are the common forms. These are genetic, often passed down through families, and often entirely asymptomatic, many people live full lives without knowing they carry a trait. They show up on labs because the red cells they produce are structurally different and smaller than average.',
    '**Note that this is an evaluation pattern, not a diagnosis.** Small cells with adequate iron **warrant** a hemoglobinopathy workup; they do not confirm one.',
    'Red cell distribution width (RDW) is a supporting signal: in iron deficiency, RDW tends to be elevated; in hemoglobinopathy traits, RDW tends to be normal.',
  ],
  abstractNextSteps:
    'Hemoglobin electrophoresis is the standard next test.',
  whatThisMeansIntro:
    'Hemoglobin electrophoresis is the standard next test for this pattern. Questions worth bringing to your clinician:',
  questionPromptList: [
    {
      question: 'Can we order hemoglobin electrophoresis?',
      explainer:
        'This is the test that differentiates among the hemoglobinopathy forms. It\'s the standard next step for this pattern.',
    },
    {
      question: 'Should I ask my close relatives about any known blood or iron conditions?',
      explainer:
        'Hemoglobinopathy traits are hereditary and often run in families from particular ancestral regions. A focused family history sharpens the clinician\'s read and can suggest whether siblings or children are worth screening.',
    },
    {
      question: 'Could iron deficiency also be present?',
      explainer:
        'Concurrent iron deficiency plus a hemoglobinopathy trait is common and can complicate the picture. Layered workup is sometimes indicated.',
    },
    {
      question: 'If a trait is identified, what does it mean for me?',
      explainer:
        'For most people carrying a trait, the practical impact is limited, the trait shows up on labs but usually does not cause significant problems. Pregnancy and family planning are contexts where it can matter.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} that this pattern does not fit classical iron deficiency and warrants further evaluation. Identifying the cause requires hemoglobin electrophoresis and a clinical interpretation.',
  pdfTransition:
    'Your PDF includes the full version of this read, the specific test to ask about, the questions to bring to your clinician, and the research citations behind every threshold we applied.',
};

const macrocytic_anemia_signal: PatternContent = {
  banner: {
    headline: 'This pattern sits outside our iron focus.',
    body: 'Your red blood cells are larger than average and your hemoglobin is below the reference range. That combination usually points away from iron and toward a different workup, most often vitamin B12 or folate, and it is a pattern a clinician should evaluate directly.',
  },
  overview: [
    'Your MCV (the average size of your red blood cells) came back at **{{mcv_value}}** and your hemoglobin came back at **{{hb_value}}**. MCV above 100 fL combined with hemoglobin below the reference range is what the research calls a **macrocytic anemia signal**. Iron deficiency typically produces the opposite, smaller red cells, not larger. So this pattern is unlikely to be an iron-deficiency picture.',
    'The most common causes of macrocytic anemia are **vitamin B12 deficiency** and **folate deficiency**. Other causes exist, including some medications, thyroid conditions, and certain marrow conditions. Sorting between them is a clinical call that depends on labs we don\'t collect and context we don\'t have.',
    'This tool focuses on iron. It does not interpret B12, folate, or other macrocytic causes.',
  ],
  abstractNextSteps:
    'Outside this tool\'s iron scope; B12 and folate panel is the standard next step.',
  whatThisMeansIntro:
    'Bring this result to a clinician. A few things worth asking about:',
  questionPromptList: [
    {
      question: 'Can we run a B12 and folate panel?',
      explainer:
        'These are the two most common causes.',
    },
    {
      question: 'Beyond B12 and folate, what other tests make sense given my history?',
      explainer:
        'Thyroid function and a broader CBC review can be part of the workup depending on your history.',
    },
    {
      question: 'Could any medications be contributing?',
      explainer:
        'Several common medications can produce this pattern.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in the routing. Our scope is iron, and this pattern points away from iron.',
  pdfTransition:
    'Your PDF includes the question list for the macrocytic workup and the plain-language explanation of why this pattern needs a different lane.',
};

const anemia_with_replete_ferritin: PatternContent = {
  banner: {
    headline: 'Anemia present, but iron stores look adequate.',
    body: 'Your hemoglobin is below the reference range, so this is anemia. Your ferritin is above the depletion threshold, which means iron deficiency does not explain it. Something else is causing the anemia, and **MCV** (the average size of your red blood cells) is the marker that would tell us what.',
  },
  overview: [
    'Your hemoglobin came back at **{{hb_value}}**. That sits below the reference range for your life stage, which means anemia. Major health organizations agree on this cutoff (`CONSENSUS` evidence). Your ferritin came back at **{{ferritin_value}}**. That is above the depletion threshold of 30 μg/L for non-pregnant premenopausal women, and above the **{{stage_3_threshold}}** stage-3 threshold. The ferritin cutoffs come from a large multinational analysis (Mei 2024, `HIGH QUALITY` evidence). Iron deficiency does not explain this anemia.',
    'When ferritin looks adequate but you are anemic, the research uses **MCV** (the average size of your red blood cells) as the next step. MCV points to one of three causes:',
    '- **Small cells (microcytic)**: a thalassemia trait, or iron being held back from red-cell production even though ferritin reads adequate (Cappellini 2020).\n- **Normal-size cells (normocytic)**: anemia of chronic disease, red blood cells breaking down, or recent blood loss (Skikne 2011).\n- **Large cells (macrocytic)**: B12 or folate deficiency.',
    'Without MCV or TSAT (which would test whether iron is locked away from the cells that need it), we cannot identify the cause of your anemia from these labs alone.',
  ],
  abstractNextSteps:
    'Add **MCV** (most cost-effective) or a CBC with red-cell indices to find the cause of the anemia.',
  whatThisMeansIntro:
    'Bring this read to a clinician. Specific questions worth asking:',
  questionPromptList: [
    {
      question: 'Can we add an MCV or full CBC with red-cell indices?',
      explainer:
        'MCV is the single most useful marker for telling apart different kinds of anemia. A standard CBC reports MCV alongside hemoglobin.',
    },
    {
      question: 'Should we test for B12 and folate?',
      explainer:
        'Macrocytic anemia (large red cells) most often comes from B12 or folate deficiency. If MCV comes back high, this is the next test.',
    },
    {
      question: 'Could this be anemia of chronic disease?',
      explainer:
        'Long-term inflammation can cause anemia even when iron stores are adequate. TSAT (transferrin saturation) plus a CRP would help test for this.',
    },
    {
      question: 'Is recent blood loss possible (heavy periods, GI bleeding, or surgery)?',
      explainer:
        'Hemoglobin can drop faster than ferritin in the early phase after blood loss. Your clinical context tells us the difference.',
    },
    {
      question: 'Should we recheck in a few weeks before doing a wider workup?',
      explainer:
        'Sometimes a single low hemoglobin reflects short-term factors like illness, dehydration, or when the blood was drawn. A repeat test under normal conditions can change the picture before doing more workup.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in the **signal**. The anemia is real (`HIGH QUALITY` evidence on the hemoglobin threshold), and the ferritin is genuinely above the deficiency range (`HIGH QUALITY` evidence on the ferritin thresholds). The uncertainty is in the **cause**. Without MCV or TSAT, several possible causes stay open: anemia of chronic disease, a thalassemia trait, B12 or folate deficiency, red blood cells breaking down, or recent blood loss.',
  pdfTransition:
    'Your PDF includes the full version of this read, the specific questions to bring to your clinician, and the research citations behind every threshold we applied.',
};

// -----------------------------------------------------------------------------
// Part 5. Meta / safety patterns
// -----------------------------------------------------------------------------

const mixed_picture_multiple_patterns: PatternContent = {
  banner: {
    headline: 'Your results could fit more than one pattern.',
    body: 'The research-based signals in your data do not separate cleanly enough for us to name a single read. We can show you the candidates and the test that would sort between them.',
  },
  overview: [
    'Your data fits more than one pattern with similar likelihood. We can\'t pick a single classification from what you entered. The patterns your values are closest to: **{{candidate_patterns_list}}**.',
    'This is a limitation of the information you entered. Iron labs overlap, and inflammation and life stage can shift the boundaries between patterns. That\'s the whole reason this further-investigation pathway exists.',
    'Adding **{{most_informative_next_test}}** is the marker most likely to separate these patterns for your situation. Either way, a clinician conversation is warranted so you can walk through which of these applies.',
  ],
  abstractNextSteps:
    'Adding the suggested marker is the simplest path to a clearer read.',
  whatThisMeansIntro:
    'Bring your data and this page to a clinician. Questions worth asking:',
  questionPromptList: [
    {
      question: 'Which test would most help us pick between the candidate patterns?',
      explainer:
        'The engine suggests one; your clinician may weigh differently given your history.',
    },
    {
      question: 'Does retesting under better conditions make sense before adding more markers?',
      explainer:
        'A fasting morning draw and an inflammation-free window can change several values meaningfully.',
    },
    {
      question: 'Given my history, which of these candidates would you weight higher?',
      explainer:
        'Your clinician has the full context. We do not. That is part of why the read is uncertain on our side.',
    },
  ],
  confidenceParagraph:
    'Confidence is low here, deliberately. When the data does not separate, we do not force a pattern. The research supports the cautious path of gathering one more marker, then re-reading.',
  pdfTransition:
    'Your PDF includes the candidate pattern explainers, the disambiguation guide, and the question list for a productive clinician conversation.',
};

const insufficient_data: PatternContent = {
  banner: {
    headline: 'We don\'t have enough to classify a pattern.',
    body: 'Based on the values you entered, **{{recommended_next_test}}** is the single test most likely to resolve this into a clear pattern read. Add it on your next blood draw and re-enter your values here.',
  },
  overview: [
    'Your current inputs don\'t go far enough to classify an iron pattern. The tool reads against the four core markers the research uses for women\'s iron status: **ferritin**, **hemoglobin**, **MCV** (mean corpuscular volume), and **TSAT** (transferrin saturation). When fewer of these are present, the picture stays ambiguous.',
    'Based on what you already entered, the single test most likely to resolve this is **{{recommended_next_test}}**. We checked: when we sweep plausible values for {{recommended_next_test}} against your existing inputs, the result lands on a substantive pattern often enough that adding it has the highest expected payoff. Other markers would help, but {{recommended_next_test}} is the highest-leverage starting point.',
    'If you don\'t have any recent labs at all, the simplest next step is a standard **CBC + ferritin** panel. Both are standard orders and usually easy to request.',
  ],
  abstractNextSteps:
    'Add **{{recommended_next_test}}** on your next blood draw, then re-enter your values here.',
  whatThisMeansIntro:
    'Bring the recommendation above to your clinician. Questions worth asking:',
  questionPromptList: [
    {
      question: 'Can you order **{{recommended_next_test}}** (and a CBC if I don\'t already have one) at my next blood draw?',
      explainer:
        'Based on what you already entered, this is the single test most likely to give a clear pattern read. The tool re-runs once you add the value.',
    },
    {
      question: 'Based on my history, are there other markers worth ordering at the same time?',
      explainer:
        'Menstrual pattern, pregnancy history, chronic conditions, and diet shape what makes sense beyond the basics. TSAT, CRP, or sTfR sometimes pair well with the markers you already have.',
    },
  ],
  confidenceParagraph:
    'There is no pattern to attach confidence to yet. Add **{{recommended_next_test}}** on your next blood draw, then come back and re-enter your values.',
  pdfTransition:
    'Your PDF includes a one-page summary of what your current labs do and don\'t tell you, the recommended next test for your situation, and the exact ask for your clinician. Bring it to your next appointment.',
};

const pregnancy_t1_at_risk_for_t3_deficiency: PatternContent = {
  banner: {
    headline: 'Iron stores in the at-risk band for late-pregnancy deficiency.',
    body: 'Your first-trimester ferritin is above the trimester-specific deficiency cutoff. But it is below the level a recent cohort study found protective against third-trimester deficiency. This is a `PRELIMINARY` finding from a single cohort, not yet replicated. It shows this is the window where preventive supplementation has the strongest support.',
  },
  overview: [
    'Your ferritin came back at **{{ferritin_value}}** in the first trimester. The trimester-specific iron-deficiency cutoff for the first trimester is **25.8 μg/L** (Mei 2024, `HIGH QUALITY` evidence). You are above that cutoff. Your iron stores are not in the deficient range. But McCarthy 2024, a prospective pregnancy cohort study, found that first-trimester ferritin below **60 μg/L** at around 15 weeks predicts iron deficiency by the third trimester. Your ferritin sits in that at-risk range.',
    'This is not iron deficiency. It is a level the research suggests may not be enough to cover the increased iron needs of late pregnancy without preventive support. The third trimester needs substantial iron for fetal stores and the mother\'s red-cell expansion. Starting late pregnancy with iron stores already on the lower side is what McCarthy 2024 flagged.',
    'The evidence here is `PRELIMINARY`. One careful prospective cohort study, from a single site, in a mostly Caucasian population. It has not been replicated in a second cohort yet. The finding supports preventive action in this window, even though the specific 60 μg/L threshold may shift as more data comes in.',
  ],
  abstractNextSteps:
    'Bring this read to your prenatal clinician. Preventive iron support typically starts in this band.',
  whatThisMeansIntro:
    'Your prenatal clinician (OB, midwife, or family doctor with prenatal care scope) is the right next reader. Questions worth asking:',
  questionPromptList: [
    {
      question: 'Should we start preventive iron supplementation now to keep ferritin from falling further into the third trimester?',
      explainer:
        'The McCarthy 2024 data and general pregnancy iron physiology (Bothwell 2000) support starting before the third-trimester demand surge. Your clinician will pick a form and dose appropriate to your tolerance and history.',
    },
    {
      question: 'Are there dietary or absorption factors I should adjust alongside any supplementation?',
      explainer:
        'Absorption shifts during pregnancy. Inhibitors (coffee, tea, calcium with iron) and enhancers (vitamin C, animal protein) both matter more in this window than they typically do.',
    },
    {
      question: 'When should we recheck ferritin and hemoglobin?',
      explainer:
        'A second-trimester recheck is standard. A follow-up draw 6–8 weeks after starting any preventive supplement helps confirm the trajectory is stable.',
    },
    {
      question: 'Are there any symptoms I should report sooner than the next scheduled visit?',
      explainer:
        'Worsening fatigue, breathlessness, or any unusual symptom in pregnancy is worth flagging. The iron context can change how those symptoms are read.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in the signal. The first-trimester deficiency cutoff (25.8 μg/L) rests on `HIGH QUALITY` evidence. The at-risk-for-T3 boundary at 60 μg/L is `PRELIMINARY`, based on one prospective cohort from a single site. We surface the pattern because preventive support in this window is well-supported by general pregnancy iron physiology.',
  pdfTransition:
    'Your PDF includes the full version of this read, the specific questions to bring to your prenatal clinician, and the research citations behind every threshold we applied.',
};

const no_established_pattern: PatternContent = {
  banner: {
    headline: 'Your full panel doesn\'t match an established research pattern.',
    body: 'You provided a complete panel: ferritin, hemoglobin, MCV, and TSAT. No single pattern in the published research fits this specific combination of values. This is not a problem with your data. It is a limit of what the literature has mapped to date.',
  },
  overview: [
    'The tool reads your labs against the patterns the published research recognizes: iron deficiency at each of its staged levels, anemia of chronic disease, functional iron deficiency, iron-overload routing, the morphology evaluations, and the inflammation-masked picture. Your specific combination of values does not clearly fit any of those.',
    'A few situations produce this result. Markers that usually move together can disagree (for example, deeply low ferritin with macrocytic red cells, or replete ferritin with low TSAT and no inflammation context). Values can sit right at the boundaries the research uses. Or the pattern your labs describe may simply sit outside what the iron-status literature has mapped so far.',
    'This is a different result than "we did not have enough information." We had your full panel. The research itself does not have a named pattern for this exact combination. That is information worth knowing. The read on these labs requires a clinician who can interpret your full picture, not a fixed rule.',
  ],
  abstractNextSteps:
    'Bring this read to a clinician. A hematologist or your primary care doctor, with the lab values in hand.',
  whatThisMeansIntro:
    'A clinician integrating your full picture (history, exam, other tests, context) is the right next step. Questions worth asking:',
  questionPromptList: [
    {
      question: 'Looking at all four markers together, what does this combination suggest to you?',
      explainer:
        'Clinicians read combinations the way you would read a sentence, not one word at a time. Ask them what the picture as a whole points at.',
    },
    {
      question: 'Are any of these values at a known threshold boundary where the interpretation changes?',
      explainer:
        'Some thresholds have a "gray zone" right around the cutoff. A single repeat draw can move the interpretation when you are sitting on a boundary.',
    },
    {
      question: 'Is there an additional test that would resolve which pattern applies?',
      explainer:
        'Soluble transferrin receptor (sTfR), reticulocyte hemoglobin (RetHe), or a peripheral blood smear can sometimes resolve a combination that the standard four markers cannot.',
    },
    {
      question: 'Could a recent event (illness, supplement use, transfusion, or recent blood loss) be shaping this picture?',
      explainer:
        'Lab values reflect what your body is doing at the moment of the draw. Recent context that we did not capture can change the read.',
    },
  ],
  confidenceParagraph:
    'Confidence is intentionally low. The data is fine. The research does not have an established pattern for this combination, so we will not assign one.',
  pdfTransition:
    'Your PDF includes the full version of this read, the specific questions to bring to your clinician, and the research citations behind every threshold we applied.',
};

const severity_urgent_override: PatternContent = {
  banner: {
    headline: 'See a clinician as promptly as possible.',
    body: 'One of your values sits in a range that the research flags for urgent evaluation. This is not a diagnosis. It is a flag, and the action it calls for is prompt clinical contact: your primary care doctor\'s office, an urgent-care clinic, or the nearest emergency department if you cannot reach either.',
  },
  overview: [
    'The specific trigger for this urgent flag depends on which of your values crossed a severity threshold: hemoglobin below 70 g/L (severe anemia), ferritin above 1000 μg/L combined with TSAT above 50% (possible acute iron overload), or hemoglobin below 80 g/L within six months postpartum. Evidence strength for these severity thresholds is `HIGH QUALITY`, and `CONSENSUS` on the urgent-care routing.',
    'We are not saying you have a specific condition. We are not saying the number is certain, labs can be wrong, and a confirmatory draw is often part of the clinician\'s first step. We are saying that the value you entered sits in a range where the research-based position is: do not wait for a routine appointment. See someone today.',
    'If you have a primary care doctor, call the office now, tell them your hemoglobin or ferritin and TSAT values, and ask to be seen today. If you do not have a primary care doctor, go to an urgent-care clinic with your lab report and this PDF. Go to the emergency department today if any of these apply: chest pain, shortness of breath at rest, fainting, a fast or pounding heartbeat at rest, confusion, or heavy ongoing bleeding you cannot control at home.',
  ],
  abstractNextSteps:
    'Same-day clinician contact warranted.',
  whatThisMeansIntro:
    'Bring your lab report (the original, not a screenshot), this PDF, a list of your current medications and supplements, and a brief note on when any symptoms started. Questions worth asking:',
  questionPromptList: [
    {
      question: 'Can we confirm this value with a repeat draw today or tomorrow?',
      explainer:
        'Labs can be wrong. Confirmation is often part of the clinician\'s first step.',
    },
    {
      question: 'What\'s likely driving this?',
      explainer:
        'Severe anemia, possible acute overload, and postpartum low hemoglobin each have distinct workup pathways.',
    },
    {
      question: 'Is this something that can be managed in primary care, or do I need a specialist?',
      explainer:
        'The right care setting depends on the cause and your clinical picture.',
    },
  ],
  confidenceParagraph:
    '{{confidence_lead}} in the urgency. The severity thresholds are `HIGH QUALITY` and the same-day routing is a `CONSENSUS` clinical position. We show this banner above everything else because the cost of waiting is higher than the cost of a same-day clinician contact that turns out reassuring.',
  pdfTransition:
    'Your PDF includes the values that triggered the banner, the research citations behind the thresholds, and a short question list for the clinician visit. It is the most useful thing to walk in with.',
};

// -----------------------------------------------------------------------------
// Registry, every WinnerPatternId must be keyed.
// -----------------------------------------------------------------------------

export const PATTERN_CONTENT: Record<WinnerPatternId, PatternContent> = {
  // Part 1, staged iron model
  stage_0_iron_replete,
  stage_1_incipient_id,
  stage_2_iron_depletion_no_anemia,
  stage_3_iron_deficient_erythropoiesis,
  stage_4_iron_deficiency_anemia,

  // Part 2, inflammation patterns
  inflammation_masks_iron_status,
  functional_iron_deficiency,
  anemia_of_chronic_disease_classic,

  // Part 3, overload patterns
  iron_overload_trigger_premenopausal,
  iron_overload_trigger_postmenopausal,
  very_high_ferritin_any_stage,

  // Part 4, morphology patterns
  microcytic_normal_iron_studies,
  macrocytic_anemia_signal,
  anemia_with_replete_ferritin,

  // Part 4b, life-stage-specific risk patterns
  pregnancy_t1_at_risk_for_t3_deficiency,

  // Part 5, meta / safety patterns
  mixed_picture_multiple_patterns,
  insufficient_data,
  no_established_pattern,
  severity_urgent_override,
};
